/**
 * Products Module
 *
 * API methods for product data.
 */

import { forwardFailure, get } from '../core/client';
import { getConfig, getShopId } from '../core/config';
import { buildEndpoint } from '../core/endpoint';
import type {
  SDKResponse,
  Product,
  ProductCategory,
  ProductGroup,
  ProductVariant,
  PriceVariant,
  CursorPagination,
} from '../types';
import { getStore } from './store';
import { getMergedStorefrontProducts } from '../utils/storefront-catalog';

interface ProductsResponse {
  products: Product[];
  groups?: ProductGroup[];
  pagination?: CursorPagination | null;
}

interface ProductResponse {
  product: Product;
}

const PRODUCTS_CACHE_TTL = 2 * 60 * 1000;

export interface GetStorefrontProductsPageOptions {
  cursor?: string | null;
  limit?: number;
  sort?: 'featured' | 'newest' | 'price-asc' | 'price-desc' | string | null;
  category?: string | null;
  hideOutOfStock?: boolean;
}

function getStorefrontProductsPageCategoryCacheKey(category: string | null | undefined): string {
  if (category === undefined) return 'category:unset';
  if (category === null) return 'category:null';
  return `category:${category}`;
}

function priceVariantToProductVariant(variant: PriceVariant & { stock?: number }): ProductVariant {
  return {
    id: variant.id,
    title: variant.title ?? variant.label ?? '',
    price: typeof variant.price === 'number' ? variant.price : Number(variant.price) || 0,
    stock: typeof variant.stock === 'number' ? variant.stock : undefined,
    // Dynamic Delivery (S8): carry the availability pair through. A supplier-backed
    // variant reports `stock: 0`, so dropping `orderable` here would make the mapped
    // variant look sold out. Left `undefined` when the source omits it, which keeps
    // the historical stock-only behaviour for payloads that predate the field.
    orderable: typeof variant.orderable === 'boolean' ? variant.orderable : undefined,
    supplier_backed:
      typeof variant.supplier_backed === 'boolean' ? variant.supplier_backed : undefined,
    quantity_min: variant.quantity_min,
    quantity_max: variant.quantity_max,
    quantityMin: variant.quantityMin,
    quantityMax: variant.quantityMax,
    image_id: variant.image_id,
    imageId: variant.imageId,
    cloudflare_image_id: variant.cloudflare_image_id,
    cloudflareImageId: variant.cloudflareImageId,
    image_url: variant.image_url,
    imageUrl: variant.imageUrl,
  };
}

function normalizeProduct(product: Product): Product {
  if (product.variants && product.variants.length > 0) {
    return product;
  }

  const priceVariants = product.price_variants;
  if (!Array.isArray(priceVariants) || priceVariants.length === 0) {
    return product;
  }

  return {
    ...product,
    variants: priceVariants.map((variant) =>
      priceVariantToProductVariant(variant as PriceVariant & { stock?: number })
    ),
  };
}

export async function getProducts(): Promise<SDKResponse<Product[]>> {
  const config = getConfig();
  const response = await get<ProductsResponse>(
    buildEndpoint('/v1/storefront/products/public/:storeSlug', {
      storeSlug: config.storeSlug,
    }),
    {
      buyerCurrency: true,
      cache: {
        key: `products:${config.storeSlug}`,
        ttl: PRODUCTS_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    // The public route returns every public product exactly once in the flat `products` array —
    // group-bound products (e.g. variant-style DYNAMIC products) are included there too, so no
    // group merge is needed anymore. Groups only carry `product_uniqids` references into this
    // list. getMergedStorefrontProducts just dedupes by uniqid (first entry wins).
    return {
      success: true,
      data: getMergedStorefrontProducts(response.data.products.map(normalizeProduct)),
    };
  }

  return forwardFailure(response, { data: [] });
}

export async function getStorefrontProductsPage(
  options?: GetStorefrontProductsPageOptions,
): Promise<SDKResponse<{ products: Product[]; pagination: CursorPagination | null }>> {
  const config = getConfig();
  const query = new URLSearchParams();
  if (typeof options?.cursor === 'string' && options.cursor.trim().length > 0) {
    query.set('cursor', options.cursor);
  }
  if (Number.isFinite(options?.limit)) {
    query.set('limit', String(Math.max(1, Math.floor(options?.limit ?? 0))));
  }
  if (typeof options?.sort === 'string' && options.sort.trim().length > 0) {
    query.set('sort', options.sort.trim());
  }
  if (typeof options?.category === 'string' && options.category.trim().length > 0) {
    query.set('category', options.category.trim());
  }
  if (options?.hideOutOfStock === true) {
    query.set('hide_out_of_stock', 'true');
  }
  const querySuffix = query.size > 0 ? `?${query.toString()}` : '';

  const response = await get<ProductsResponse>(
    `${buildEndpoint('/v1/storefront/products/shop/:storeSlug', {
      storeSlug: config.storeSlug,
    })}${querySuffix}`,
    {
      buyerCurrency: true,
      cache: {
        key: `products:page:${config.storeSlug}:${options?.limit ?? 'default'}:${options?.cursor ?? 'start'}:${options?.sort ?? 'featured'}:${getStorefrontProductsPageCategoryCacheKey(options?.category)}:${options?.hideOutOfStock === true ? 'in-stock' : 'all-stock'}`,
        ttl: PRODUCTS_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: {
        products: response.data.products.map(normalizeProduct),
        pagination: response.data.pagination ?? null,
      },
    };
  }

  return forwardFailure(response);
}

export async function getProduct(
  idOrSlug: string
): Promise<SDKResponse<Product>> {
  // Slug lookups need the current shop id. Resolve it lazily so callers do not
  // have to remember to call getStore()/getStorefront() first.
  let shopId = getShopId();
  if (!shopId) {
    const store = await getStore();
    if (!store.success) {
      // The store lookup failed for a reason the caller must see (a refused
      // currency, a suspended shop); a product read without the shop id would
      // only turn that into a misleading not-found.
      return forwardFailure(store);
    }
    shopId = store.data?.id ?? null;
  }

  const queryParams = shopId ? `?slug_shop_id=${encodeURIComponent(shopId)}` : '';

  const response = await get<ProductResponse>(
    `${buildEndpoint('/v1/storefront/products/unique/:idOrSlug', { idOrSlug })}${queryParams}`,
    {
      buyerCurrency: true,
      cache: {
        key: `product:${idOrSlug}:${shopId ?? 'no-shop'}`,
        ttl: PRODUCTS_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data?.product) {
    return {
      success: true,
      data: normalizeProduct(response.data.product),
    };
  }

  return forwardFailure(response);
}

export async function getCategories(): Promise<SDKResponse<string[]>> {
  const products = await getProducts();

  if (!products.success || !products.data) {
    return forwardFailure(products);
  }

  const categories = new Set<string>();
  for (const product of products.data) {
    if (product.categories) {
      for (const category of product.categories) {
        if (typeof category === 'string') {
          categories.add(category);
        } else if (category && typeof category === 'object' && 'uniqid' in category) {
          categories.add((category as ProductCategory).uniqid);
        }
      }
    }
  }

  return {
    success: true,
    data: Array.from(categories),
  };
}
